import { CircularProgress } from "@material-ui/core";
import Fade from "@material-ui/core/Fade";
import clsx from "clsx";
import firebase from "firebase/app";
import { useTranslation } from "next-i18next";
import Image from "next/image";
import { useRouter } from "next/router";
import React, { useState } from "react";

import { AnalyticsEventEnum } from "@/consts/AnalyticsEventEnum";
import { useUserContext } from "@/features/user/contexts/useUserContext";
import {
  PostInfoWithLoggedInUserFragment,
  PostPagePostFragment,
  PostTypeEnum,
} from "@/generated/generated-hooks";

import { HoverSubscribeMask } from "./HoveringMask/HoverSubscribeMask";
import { OwnerIndicator } from "./OwnerIndicator";

type ImageAreaProps = {
  post: PostPagePostFragment & Partial<PostInfoWithLoggedInUserFragment>;
};

export const ImageArea: React.FC<ImageAreaProps> = ({ post }) => {
  const {
    title,
    previewImage,
    images,
    slug,
    userBlurLevel,
    postBlurLevel,
    ownerBlurImageUrl,
    writerId,
    _id,
    postType,
    videoUri,
    subscriptionPlanId,
  } = post;

  const { t } = useTranslation(["post"]);
  const [hoverSubscribe, setHoverSubscribe] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const router = useRouter();
  const { user } = useUserContext();

  const hasImage = !!previewImage || images?.length > 0;
  const isOwner = user?._id === writerId;
  const isVideo = postType === PostTypeEnum.Video;

  const getCoverImage = () => {
    if (!hasImage) return null;
    if (!images) {
      return { ...previewImage };
    }

    return {
      ...images[0],
      url: ownerBlurImageUrl ?? images[0].url,
    };
  };

  const getHoverImageUrl = () => {
    if (!hasImage || !isOwner || !images) return null;
    return images[0].url;
  };

  const coverImage = getCoverImage();
  const hoverImageUrl = getHoverImageUrl();

  const onClickImage = () => {
    firebase.analytics().logEvent(AnalyticsEventEnum.ImageCardClickImage, {
      postSlug: slug,
      postId: _id,
    });

    router.push(
      {
        pathname: router.pathname,
        query: {
          ...router.query,
          preview: slug,
        },
      },
      undefined,
      { shallow: true },
    );
  };

  if (!coverImage) return null;

  //do not optimize subscription content
  const unoptimized = userBlurLevel === 0 && postBlurLevel > 0;

  const hoveringOnImage = !loading && hoverSubscribe;

  const ratio =
    coverImage.intrinsicSize.width / coverImage.intrinsicSize.height;

  return (
    <div
      className="touchable"
      onClick={onClickImage}
      onMouseEnter={(e) => setHoverSubscribe(true)}
      onMouseLeave={(e) => setHoverSubscribe(false)}
    >
      {coverImage.url && (
        <div
          className={clsx(
            "relative",
            ratio > 2 && "min-h-[240px]",
            coverImage.hasAlpha && "bg-checkerboard-pattern",
          )}
        >
          {loading && (
            <div className="flex absolute w-full h-full justify-center items-center bg-gray-200">
              <CircularProgress />
            </div>
          )}

          {/* owner indicator */}
          {isOwner && <OwnerIndicator isPublicContent={!subscriptionPlanId} />}

          <div className="absolute top-0 right-0 left-0 bottom-0 z-20">
            <HoverSubscribeMask post={post} logEventSource="image-card" />

            {isOwner && hoverImageUrl && !isVideo && (
              <Fade
                in={hoveringOnImage}
                timeout={500}
                mountOnEnter
                unmountOnExit
              >
                <div className="absolute top-0 right-0 left-0 bottom-0">
                  <Image
                    alt={title}
                    width={
                      ratio > 2 ? undefined : coverImage.intrinsicSize.width
                    }
                    height={
                      ratio > 2 ? undefined : coverImage.intrinsicSize.height
                    }
                    src={hoverImageUrl}
                    layout={ratio > 2 ? "fill" : "responsive"}
                    onMouseDown={(e) => e.preventDefault()}
                    priority
                    unoptimized
                    objectFit="cover"
                  />
                </div>
              </Fade>
            )}
          </div>

          {isVideo && videoUri && (
            <div className="z-10 absolute top-0 left-0 w-full h-full flex justify-center items-center">
              <Image
                alt="play-icon"
                src="/logo/video-player-default.png"
                layout="fixed"
                width={60}
                height={60}
              />
            </div>
          )}

          {isVideo && videoUri && subscriptionPlanId && (
            <div className="z-10 absolute bottom-3 right-3">
              <div className="bg-blackopacity-70 rounded-100 px-3 py-1.5 text-white text-sm">
                {isOwner
                  ? t("post:imageCard.myVideo")
                  : t("post:imageCard.unlocked")}
              </div>
            </div>
          )}

          {/* Image to Public */}
          <Image
            alt={title}
            src={coverImage.url}
            layout={ratio > 2 ? "fill" : "responsive"}
            onLoad={() => setLoading(false)}
            onMouseDown={(e) => e.preventDefault()}
            priority
            unoptimized={unoptimized}
            width={ratio > 2 ? undefined : coverImage.intrinsicSize.width}
            height={ratio > 2 ? undefined : coverImage.intrinsicSize.height}
            objectFit={ratio > 2 ? "cover" : undefined}
          />
        </div>
      )}
    </div>
  );
};
